/**
 * The add-account drawer's step tree (Settings → Accounts → Add account).
 *
 * Two steps, and the tree exists to make that structural rather than derived. The drawer used to
 * compute its screen as `selected ? 'configure' : 'type'` — a boolean wearing a step's clothes — and
 * hand-write its own position label, its own Back condition and its own "is there a primary here"
 * rule. Every one of those is a property of the shape below, and `footerState` reads them off it.
 *
 * ## Why the second step's title is a default, not the last word
 *
 * `configure` renders one of three screens depending on the chosen definition's `lookoutSupport`: a
 * schema-driven form (`form`), a "Connect with <platform>" consent button (`oauth`), or a link out to
 * the platform for a definition Lookout cannot relay (`external`). Only the first is honestly called
 * "Configure", so the drawer names the screen once it knows the definition and falls back to this
 * title before then. That is the same division `doneLabel` already has with a reported primary: the
 * tree owns STRUCTURE, the shell owns what the current screen is called.
 *
 * They are not three sibling steps because the split is not navigational — nothing branches, the
 * back-edge is identical for all three, and `AddAccountBody` already picks between them from the same
 * `selected` the drawer holds. Three keys would make the tree restate a choice made elsewhere, and
 * `AddAccountBody.step` is shared with the activation wizard, whose tree would have to learn them too.
 *
 * ## Why there is no step counter
 *
 * There is nowhere for one to come from, on purpose. `WizardTree` branches, so "of N" has no single
 * answer — the activation tree leaves its account step by three different routes — and a helper that
 * only worked on non-branching trees would be a trap waiting for the first branch. Shells show the
 * step's TITLE instead, which is what the activation sheet has always done.
 */
import type { WizardTree } from '@/_shared/lib/wizard/types';

export type AddAccountStepKey = 'select-account-type' | 'configure';

export const ADD_ACCOUNT_TREE: WizardTree<AddAccountStepKey> = [
  {
    // `menu`, not `form`: picking an account-type row IS the move, so the step has nothing to submit
    // and `footerState` gives it no primary. As the root it has nothing behind it either, which is
    // what leaves it with no footer at all — the drawer's close is in the header.
    key: 'select-account-type',
    title: 'Select account type',
    kind: 'menu',
    children: [
      {
        // `form`, because it submits: the body reports its own primary ("Save", "Connect with
        // <platform>") up to the drawer, exactly as it already does inside the activation wizard.
        key: 'configure',
        title: 'Configure',
        kind: 'form',
      },
    ],
  },
];
